import React, { useCallback, useMemo, useState } from 'react';
import { RadioButton } from 'react-native-paper';
import { DrawerNavigationProp } from '@react-navigation/drawer';
import { Formik } from 'formik';
import * as yup from 'yup';

import { FormikHelpers } from 'formik/dist/types';
import Container from '../../components/Container';
import Typography from '../../components/Typography';
import FormikInput from '../../components/formik/FormikInput';
import { useModal } from '../../context/ModalContext';
import * as adminApi from '../../apis/adminApis';

import Radio from '../../components/ui/Radio';
import ScrollablePage from '../../components/ui/ScrollablePage';
import { AdminConfirmModalContent, RawAdminCommandOutput, SendAdminCommandButton } from './AdminTools';
import { UserRole } from '../../types/appsync-types';

interface RequestForm {
  phone: string;
  userRole: UserRole;
}

export default function SuspendUser({ navigation }: { navigation: DrawerNavigationProp<any> }) {
  const [initForm] = useState<RequestForm>({
    phone: '',
    userRole: UserRole.USER,
  });
  const [request, setRequest] = useState<Promise<any>>();

  const modal = useModal();

  const validateSchema = useMemo(() => {
    return yup.object().shape({
      phone: yup.string().required(),
      userRole: yup.string().required(),
    });
  }, []);

  const handleAction = useCallback(
    (form: RequestForm) => {
      const p = adminApi.userSetRole(form.phone, form.userRole);
      setRequest(p);
      p.finally(() => modal.closeModal());
      return p;
    },
    [modal],
  );

  const handleSubmitForm = useCallback(
    async (form: RequestForm, formikHelper: FormikHelpers<RequestForm>) => {
      modal.openModal({
        content: <AdminConfirmModalContent onDismiss={() => modal.closeModal()} onConfirm={() => handleAction(form)} />,
      });
    },
    [modal, handleAction],
  );

  return (
    <ScrollablePage
      summary={{
        title: 'Set user role',
        description: 'Change the access level of the user',
        icon: 'pencil',
      }}
    >
      <Container>
        <Formik initialValues={initForm} onSubmit={handleSubmitForm} validationSchema={validateSchema}>
          {({ values, handleChange, handleSubmit, isSubmitting }) => (
            <>
              <FormikInput
                name={'phone'}
                mode="outlined"
                keyboardType="phone-pad"
                autoComplete="off"
                autoCapitalize="sentences"
                label={'Phone number (e.g. +12345123)'}
              />
              <Typography variant="body2">Status</Typography>
              <RadioButton.Group onValueChange={handleChange('userRole')} value={values.userRole}>
                <Radio label={UserRole.USER} value={UserRole.USER} />
                <Radio label={UserRole.SUPER_ADMIN} value={UserRole.SUPER_ADMIN} />
              </RadioButton.Group>

              <SendAdminCommandButton handleSubmit={handleSubmit} isSubmitting={isSubmitting} />
            </>
          )}
        </Formik>
        <RawAdminCommandOutput request={request} />
      </Container>
    </ScrollablePage>
  );
}
